import {assignRecursively} from '#core/utils/config-utils.js';

const MIGRATION = [
    {
        version: 25070000, // Version format is numeric 25XXYYZZZ
        doMigration: migrate_to25_7,
    },
    {
        version: 25120001,
        doMigration: migrate_to26_00,
    },
];

let _isSomeConfigsMigrated = false;

export function isSomeConfigMigrated() {
    return _isSomeConfigsMigrated;
}
/**
 * Migrate config
 * @param config {object} Config to update
 * @param log Logger (optional)
 * @param migrationList {array} Migration list
 * @return {object} mutated input config
 */
export function migrateConfig(config, log, migrationList = MIGRATION) {
    if (Object.keys(config).length === 0) {
        // Empty object, nothing to migrate
        return config;
    }

    try {
        const version = parseVersion(config?.version?.version);

        const result = applyMigrations(config, version, migrationList, log);
        delete result.version; // version is not a real config parameter, but just a build info

        return result;
    } catch (error) {
        log?.error('Migration failed:', error.message);
    }
}

// Should return numeric version in format 250600
function parseVersion(versionString = '25.0.0.0') {
    const VERY_OLD_VERSION = 25000000;

    if (typeof versionString !== 'string') {
        return VERY_OLD_VERSION;
    }

    const parts = versionString.split('.');
    if (parts.length !== 4) {
        return VERY_OLD_VERSION;
    }

    return parts.reduce((result, part) => {
        let numeric = parseInt(part, 10);
        if (isNaN(numeric)) {
            numeric = 0; // ignore invalid parts
        }

        return result * 100 + numeric;
    }, 0);
}

function applyMigrations(config, configVersion, migrationList, log) {
    for (let i = 0; i < migrationList.length; i++) {
        if (configVersion <= migrationList[i].version) {
            for (let j = i; j < migrationList.length; j++) {
                config = migrationList[j].doMigration(config);
                log?.info(
                    '%cConfig migrated to version',
                    'color: dodgerblue; font-weight: bold',
                    migrationList[j].version
                );
                _isSomeConfigsMigrated = true;
            }
            break;
        }
    }

    return config;
}

function renameKeys(config, migrationMap) {
    for (const [oldKey, newKey] of Object.entries(migrationMap)) {
        if (config.hasOwnProperty(oldKey)) {
            if (!config.hasOwnProperty(newKey)) {
                // If new key does not exist, just copy value
                config[newKey] = config[oldKey];
            }
            delete config[oldKey];
        }
    }
}

function migrate_to25_7(config) {
    const palette = config?.palette?.BASIC;
    if (!palette) {
        return config;
    }
    const migrationMap = {
        DEFAULT_PAGE_BG: 'FILL_PAGE',
        DEFAULT_HEADER_BG: 'FILL_HEADER',
        DEFAULT_STYLIZED_BG: 'FILL_HERO',
        DEFAULT_SHEET_BG: 'FILL_SHEET',
        DEFAULT_POPOVER_BG: 'FILL_POPOVER',
        DEFAULT_PROMO_CARD_BG: 'FILL_CARD',
        DEFAULT_CONTROLS_BORDER_COLOR: 'FILL_CONTROL_OUTLINE',
        DEFAULT_ODDS_BG: 'FILL_CONTROL_ODDS',
        DEFAULT_ODDS_SELECTED_BG: 'FILL_SELECTED_ODDS',
        DEFAULT_SELECTION_BG: 'FILL_SELECTED_PRIMARY',
        DEFAULT_PRIMARY_BUTTON_COLOR: 'FILL_ACCENT_PRIMARY',
        DEFAULT_LIVE_COLOR: 'TEXT_ACCENT_FILL',
    };
    renameKeys(palette, migrationMap);

    return config;
}

function migrate_to26_00(config) {
    if (!config) return config;

    const palette = config.palette;
    config.appearance = config.appearance || {};
    assignRecursively(config.appearance, palette);
    renameKeys(config.appearance, {
        BASIC: 'BASE_PALETTE',
    });

    return config;
}
